<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <!-- Header Section -->
      <div class="d-flex mb-4 align-center flex-wrap">
        <h2>{{ loading ? (isNaccuUser ? $t('memberDataSection.loadingFactory') : $t('memberDataSection.loadingBuyingStation')) : (buyingStationData.name || (isNaccuUser ? 'Factory' : 'Buying Station')) }}</h2>
        <v-spacer></v-spacer>
        <div class="d-flex align-center">
          <v-btn color="primary" dark class="mr-2 mb-2" @click="handleAssociatedFarms">
            {{ $t("memberDataSection.associatedFarms") }}
          </v-btn>
          <v-btn color="primary" dark class="mr-2 mb-2" @click="handleEdit" v-if="getModulePermittedActions.put">
            {{ $t("memberDataSection.edit") }}
          </v-btn>
          <v-btn color="primary" dark class="mr-2 mb-2" @click="handleBack">
            {{ $t("memberDataSection.back") }}
          </v-btn>
        </div>
      </div>

      <!-- Tabbed Sections -->
      <v-card elevation="0" class="bs-detail-tabs-card">
        <!-- Tab Bar -->
        <v-tabs
          v-model="activeTab"
          class="bs-detail-tabs"
          background-color="transparent"
          :grow="false"
          show-arrows
        >
          <v-tab class="text-capitalize">{{ isNaccuUser ? $t("memberDataSection.factoryDetails") : $t("memberDataSection.buyingStationDetails") }}</v-tab>
          <v-tab class="text-capitalize">{{ purchaseOrderLabel }}</v-tab>
          <v-tab class="text-capitalize">{{ $t("memberDataSection.processingBatch") }}</v-tab>
          <v-tab class="text-capitalize">{{ $t("memberDataSection.parchmentDelivery.listTitle") }}</v-tab>
          <v-tab v-if="canShowConnectedUsers" class="text-capitalize">{{ isNaccuUser ? $t("memberDataSection.factoryUsers") : $t("memberDataSection.buyingStationUsers") }}</v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-items v-model="activeTab">

          <!-- Tab 0: Buying Station Details -->
          <v-tab-item>
            <v-card elevation="0" class="px-5 pt-4 pb-4">
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <div class="mb-3">
                    <span>{{ $t("memberDataSection.name") }}</span>
                    <div class="font-weight-bold mt-3">{{ buyingStationData.name || '-' }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="9">
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.email") }}</span>
                        <div class="font-weight-bold mt-3">{{ buyingStationData.email || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.phoneNumber") }}</span>
                        <div class="font-weight-bold mt-3">{{ buyingStationData.phoneNumber || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.country") }}</span>
                        <div class="font-weight-bold mt-3">{{ buyingStationData.country || '-' }}</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3">
                  <div class="mb-3">
                    <span>{{ $t("memberDataSection.provinceState") }}</span>
                    <div class="font-weight-bold mt-3">{{ provinceOrState || '-' }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="9">
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.villageCityTown") }}</span>
                        <div class="font-weight-bold mt-3">{{ villageCityOrTown || '-' }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.role") }}</span>
                        <div class="font-weight-bold mt-3">{{ buyingStationData.role || (isNaccuUser ? 'Factory' : 'Buying Station') }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="mb-3">
                        <span>{{ $t("memberDataSection.status") }}</span>
                        <div class="mt-2 d-flex align-start">
                          <v-btn x-small rounded min-width="80" depressed class="green-button" height="28"
                              v-if="buyingStationData.status === 'active'">
                              {{ $t("memberDataSection.active") }}
                          </v-btn>
                          <v-btn x-small rounded min-width="80" depressed class="red-button" height="28"
                              v-else-if="buyingStationData.status === 'deactivated'">
                              {{ $t("memberDataSection.deactivated") }}
                          </v-btn>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- Tab 1: Purchase Orders -->
          <v-tab-item>
            <v-card elevation="0" class="px-5 pt-4 pb-4">
              <div class="d-flex align-center mb-4">
                <span class="text-h6 font-weight-bold">{{ purchaseOrderLabel }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  dark
                  v-if="getModulePermittedActions.post"
                  @click="handleAddPurchaseOrder"
                >
                  {{ isNaccuUser ? $t("memberDataSection.addCherryCollection") : $t("memberDataSection.addPurchaseOrder") }}
                </v-btn>
              </div>
              <PurchaseOrderList
                ref="purchaseOrderList"
                :buying-station-id="buyingStationId"
                embedded
              />
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Processing Batch -->
          <v-tab-item>
            <v-card elevation="0" class="px-5 pt-4 pb-4">
              <div class="d-flex align-center mb-4">
                <span class="text-h6 font-weight-bold">{{ $t("memberDataSection.processingBatch") }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  dark
                  v-if="getModulePermittedActions.post"
                  @click="handleAddProcessingBatch"
                >
                  {{ $t("memberDataSection.addProcessingBatch") }}
                </v-btn>
              </div>
              <ProcessingBatchList
                ref="processingBatchList"
                :buying-station-id="buyingStationId"
                embedded
              />
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Parchment Delivery -->
          <v-tab-item>
            <v-card elevation="0" class="px-5 pt-4 pb-4">
              <div class="d-flex align-center mb-4">
                <span class="text-h6 font-weight-bold">{{ $t("memberDataSection.parchmentDelivery.listTitle") }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  dark
                  v-if="getModulePermittedActions.post"
                  @click="handleAddParchmentDelivery"
                >
                  {{ $t("memberDataSection.parchmentDelivery.addParchmentDelivery") }}
                </v-btn>
              </div>
              <ParchmentDeliveryList
                ref="parchmentDeliveryList"
                :buying-station-id="buyingStationId"
                embedded
              />
            </v-card>
          </v-tab-item>

          <!-- Tab 4: Factory Users / Buying Station Users (NACCU-only) -->
          <v-tab-item v-if="canShowConnectedUsers">
            <v-card elevation="0" class="px-5 pt-4 pb-4">
              <div class="d-flex align-center mb-4">
                <span class="text-h6 font-weight-bold">{{ isNaccuUser ? $t('memberDataSection.factoryUsers') : $t('memberDataSection.buyingStationUsers') }}</span>
                <v-spacer></v-spacer>
                <v-btn color="primary" dark @click="addUserDialog = true">{{ $t('memberDataSection.addNewUser') }}</v-btn>
              </div>
              <div class="d-flex flex-wrap align-center mb-3" style="gap: 16px">
                <v-text-field v-model="connectedSearch" prepend-inner-icon="mdi-magnify" outlined dense hide-details
                  :placeholder="$t('search')" style="max-width: 280px"></v-text-field>
                <v-select v-model="connectedRoleFilter" :items="connectedRoleFilterItems" outlined dense hide-details
                  :label="$t('memberDataSection.userRole')" style="max-width: 200px"></v-select>
                <v-select v-model="connectedAccessFilter" :items="connectedAccessFilterItems" outlined dense hide-details
                  clearable :label="$t('memberDataSection.accessLevel')" style="max-width: 200px"></v-select>
                <v-spacer></v-spacer>
                <div v-if="displayedTotalRows" class="d-flex align-center">
                  <div class="py-1 title">{{ connectedFrom }} - {{ connectedTo }} {{ $t('of') }} {{ displayedTotalRows }}</div>
                  <v-btn class="mx-2" fab small outlined color="primary" :disabled="connectedUsersPage <= 1" @click="connectedUsersPage--">
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn class="mx-2" fab small outlined color="primary" :disabled="connectedUsersPage >= connectedTotalPages" @click="connectedUsersPage++">
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-data-table :headers="connectedUserHeaders" :items="displayedConnectedUsers" :loading="connectedUsersLoading"
                :items-per-page="-1" hide-default-footer item-key="id" :item-class="connectedRowClass">
                <template #item.nameAndId="{ item }">
                  <div>
                    <div v-if="item.isPrimary" class="primary--text font-weight-bold caption">{{ $t('memberDataSection.primaryUser') }}</div>
                    <div>{{ item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ') }}</div>
                    <div class="caption grey--text">{{ item.id_number || item.id }}</div>
                  </div>
                </template>
                <template #item.userRole>Farmer</template>
                <template #item.accessLevel="{ item }">
                  <div v-if="item.isPrimary">{{ formatAccessLevel(item.accessLevel) }}</div>
                  <v-select v-else :key="`acc-${item.id}-${item.accessLevel}-${accessLevelSelectKey}`" :value="item.accessLevel"
                    :items="accessLevelEditItems" outlined dense hide-details
                    style="max-width: 160px" :loading="updatingAccessLevelId === item.id"
                    :disabled="updatingAccessLevelId === item.id" @change="(v) => promptAccessLevelChange(item, v)"></v-select>
                </template>
                <template #item.createdAt="{ item }">{{ getDateFormat(item.createdAt) }}</template>
                <template #item.active="{ item }">
                  <v-btn small rounded depressed min-width="100" height="34" :class="item.active ? 'green-button' : 'red-button'">
                    {{ item.active ? $t('memberDataSection.active') : $t('memberDataSection.inactive') }}
                  </v-btn>
                </template>
                <template #no-data>
                  <div class="text-center py-6 grey--text">{{ $t('memberDataSection.noConnectedUsers') }}</div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </v-container>

    <ParchmentDeliveryAddDialog
      v-model="addParchmentDialogVisible"
      :buying-station-id="buyingStationId"
      @saved="onParchmentDeliverySaved"
      @close="addParchmentDialogVisible = false"
    />

    <!-- Edit App User Dialog -->
    <EditAppUser 
      v-if="editUserDialog && selectedBuyingStation"
      :dialogU="editUserDialog" 
      :user="selectedBuyingStation"
      :isMarketPlaceUser="false"
      @closeUpdateUser="closeEditUserDialog"
      @appUserUpdated="handleUserUpdated"
    />

    <!-- Add connected user to this station (NACCU) -->
    <AddFactoryUser
      v-if="canShowConnectedUsers"
      :dialog="addUserDialog"
      :buyingStationId="$route.params.id"
      @close="addUserDialog = false"
      @userAdded="onConnectedUserAdded"
    />

    <!-- Confirm access-level change (prevents accidental updates) -->
    <ConfirmBox
      :dialog="accessLevelConfirm.show"
      :title="$t('memberDataSection.changeAccessLevel')"
      :message="accessLevelConfirmMessage"
      :cancelBtnText="$t('cancel')"
      :confirmBtnText="$t('confirm')"
      @cancel:action="cancelAccessLevelChange"
      @continue:action="confirmAccessLevelChange"
    />

  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import DateMixin from "@/mixins/DateMixin";
import moment from 'moment';
import RoleMixin from "@/mixins/RoleMixin";
import { isKenyaClient, isIndonesianClient, canRegisterBuyingStation } from "@/utils";
import EditAppUser from "@/views/user/EditAppUser.vue";
import AddFactoryUser from "@/views/user/naccu-user/AddFactoryUser.vue";
import BuyingStationService from "@/_services/BuyingStationService";
import ParchmentDeliveryList from "@/views/coffee/buying-station/parchment-deliveries/ParchmentDeliveryList.vue";
import ParchmentDeliveryAddDialog from "@/views/coffee/buying-station/parchment-deliveries/components/AddDialog.vue";
import PurchaseOrderList from "@/views/coffee/buying-station/purchase-order/PurchaseOrderList.vue";
import ProcessingBatchList from "@/views/coffee/buying-station/processing-batch/ProcessingBatchList.vue";
import ConfirmBox from "@/components/ConfirmBox";

export default {
  // Remember whether we arrived from another in-app page so Back pops history instead of
  // pushing a new list entry (which stacks up and makes browser-Back feel like a loop).
  beforeRouteEnter(to, from, next) {
    next((vm) => { vm.cameFromApp = !!(from && from.name); });
  },
  components: {
    EditAppUser,
    AddFactoryUser,
    ParchmentDeliveryList,
    ParchmentDeliveryAddDialog,
    PurchaseOrderList,
    ProcessingBatchList,
    ConfirmBox,
  },
  created() {
    this.getBuyingStationDetails();
    if (this.canShowConnectedUsers) this.getConnectedUsers();
  },
  watch: {
    connectedSearch() {
      // Debounce search input, then refetch from page 1.
      clearTimeout(this.connectedSearchTimer);
      this.connectedSearchTimer = setTimeout(() => {
        this.connectedUsersPage = 1;
        this.getConnectedUsers();
      }, 400);
    },
    connectedAccessFilter() {
      this.connectedUsersPage = 1;
      this.getConnectedUsers();
    },
    connectedUsersPage() {
      this.getConnectedUsers();
    },
  },
  computed: {
    canShowConnectedUsers() {
      // Connected-users table is part of the NACCU-only buying-station feature.
      return canRegisterBuyingStation();
    },
    connectedRoleFilterItems() {
      return [{ text: this.$t("all"), value: "all" }];
    },
    connectedAccessFilterItems() {
      // Values match raw accessLevel from the connected-users endpoint; labels via formatAccessLevel.
      return ["owner", "admin", "clerk"].map((v) => ({ text: this.formatAccessLevel(v), value: v }));
    },
    accessLevelEditItems() {
      // Editable levels for staff rows (owner is fixed to the station and not selectable).
      return ["admin", "clerk"].map((v) => ({ text: this.formatAccessLevel(v), value: v }));
    },
    connectedTotalPages() {
      return Math.max(1, Math.ceil(this.connectedTotalRows / this.connectedUsersPerPage));
    },
    connectedFrom() {
      return this.connectedTotalRows ? (this.connectedUsersPage - 1) * this.connectedUsersPerPage + 1 : 0;
    },
    connectedTo() {
      return Math.min(this.connectedUsersPage * this.connectedUsersPerPage, this.connectedTotalRows);
    },
    // The station itself is the Owner / primary user, built from the already-loaded station data.
    ownerRow() {
      const d = this.buyingStationData || {};
      return {
        id: d.id || this.$route.params.id,
        fullName: d.name,
        firstName: d.firstName,
        lastName: d.lastName,
        email: d.email,
        id_number: d.id_number,
        createdAt: d.createdAt || null,
        active: d.active !== undefined ? d.active : true,
        isPrimary: true,
        accessLevel: "owner",
      };
    },
    ownerMatchesFilter() {
      if (this.connectedAccessFilter && this.connectedAccessFilter !== "owner") return false;
      const term = (this.connectedSearch || "").trim().toLowerCase();
      if (!term) return true;
      const o = this.ownerRow;
      const name = (o.fullName || [o.firstName, o.lastName].filter(Boolean).join(" ")).toLowerCase();
      return name.includes(term) || (o.email || "").toLowerCase().includes(term);
    },
    // Add the owner row on page 1 only if the backend didn't already return a primary row.
    ownerAddedByFe() {
      return this.connectedUsersPage === 1
        && !!(this.buyingStationData && this.buyingStationData.id)
        && !this.connectedUsers.some((u) => u.isPrimary)
        && this.ownerMatchesFilter;
    },
    displayedConnectedUsers() {
      const rows = [...this.connectedUsers];
      if (this.ownerAddedByFe) rows.unshift(this.ownerRow);
      return rows;
    },
    displayedTotalRows() {
      return this.connectedTotalRows + (this.ownerAddedByFe ? 1 : 0);
    },
    connectedUserHeaders() {
      return [
        { text: this.$t("memberDataSection.userNameAndId"), value: "nameAndId", sortable: false },
        { text: this.$t("memberDataSection.email"), value: "email", sortable: false },
        { text: this.$t("memberDataSection.userRole"), value: "userRole", sortable: false },
        { text: this.$t("memberDataSection.accessLevel"), value: "accessLevel", sortable: false },
        { text: this.$t("memberDataSection.userRegistrationDate"), value: "createdAt", sortable: false },
        { text: this.$t("memberDataSection.status"), value: "active", sortable: false },
      ];
    },
    isNaccuUser() {
      return isKenyaClient();
    },
    purchaseOrderLabel() {
      return isKenyaClient() ? this.$t("memberDataSection.cherryCollection") : this.$t("memberDataSection.purchaseOrder");
    },
    isIndonesianUser() {
      return isIndonesianClient();
    },
    isOwner() {
      return this.getCurrentRoles.some(x => ["operator_owner", "supplier_owner"].includes(x))
    },
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    breadcrumbs() {
      return [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.isNaccuUser ? this.$t("memberDataSection.factories") : this.$t("memberDataSection.buyingStation"),
          disabled: false,
          to: { name: 'CoffeeBuyingStation' },
        },
        {
          text: this.buyingStationData.name
            || (this.isNaccuUser ? this.$t("memberDataSection.factoryDetails") : this.$t("memberDataSection.buyingStationDetails")),
          disabled: true,
        },
      ];
    },
    provinceOrState() {
      return this.buyingStationData.stateId || this.buyingStationData.district || '-';
    },
    villageCityOrTown() {
      return this.buyingStationData.city || this.buyingStationData.village || this.buyingStationData.district || '-';
    },
    buyingStationId() {
      return this.$route.params.id;
    },
    accessLevelConfirmMessage() {
      const item = this.accessLevelConfirm.item;
      if (!item) return "";
      return this.$t("memberDataSection.changeAccessLevelConfirm", {
        name: item.fullName || item.email,
        level: this.formatAccessLevel(this.accessLevelConfirm.level),
      });
    },
  },
  data() {
    return {
      cameFromApp: false,
      loading: true,
      activeTab: 1,
      selectedBuyingStation: null,
      buyingStationData: {},
      addParchmentDialogVisible: false,
      editUserDialog: false,
      connectedUsers: [],
      connectedUsersLoading: false,
      addUserDialog: false,
      connectedUsersPage: 1,
      connectedUsersPerPage: 10,
      connectedTotalRows: 0,
      updatingAccessLevelId: null,
      accessLevelConfirm: { show: false, item: null, level: null },
      accessLevelSelectKey: 0,
      connectedSearch: "",
      connectedRoleFilter: "all",
      connectedAccessFilter: null,
      connectedSearchTimer: null,
    };
  },
  methods: {
    async getConnectedUsers() {
      this.connectedUsersLoading = true;
      try {
        const params = {
          page: this.connectedUsersPage,
          limit: this.connectedUsersPerPage,
        };
        if (this.connectedSearch) params.search = this.connectedSearch.trim();
        if (this.connectedAccessFilter) params.accessLevel = this.connectedAccessFilter;
        const res = await BuyingStationService.getConnectedUsers(this.$route.params.id, params);
        this.connectedUsers = (res.data && res.data.rows) || [];
        this.connectedTotalRows = (res.data && res.data.numRows) || 0;
      } catch (e) {
        this.connectedUsers = [];
        this.connectedTotalRows = 0;
      } finally {
        this.connectedUsersLoading = false;
      }
    },
    promptAccessLevelChange(item, level) {
      if (!level || item.accessLevel === level) return;
      this.accessLevelConfirm = { show: true, item, level };
    },
    confirmAccessLevelChange() {
      const { item, level } = this.accessLevelConfirm;
      this.accessLevelConfirm = { show: false, item: null, level: null };
      if (item && level) this.updateAccessLevel(item, level);
    },
    cancelAccessLevelChange() {
      // Bump the select key so the dropdown remounts and snaps back to the unchanged accessLevel
      // (Vuetify keeps its own internal selection after a pick; re-rendering the row alone won't reset it).
      this.accessLevelConfirm = { show: false, item: null, level: null };
      this.accessLevelSelectKey++;
    },
    async updateAccessLevel(item, accessLevel) {
      if (!accessLevel || item.accessLevel === accessLevel) return;
      this.updatingAccessLevelId = item.id;
      try {
        const res = await BuyingStationService.updateConnectedUserAccessLevel(
          this.$route.params.id, item.id, accessLevel
        );
        if (res && res.success) {
          item.accessLevel = accessLevel;
          this.$notify({ text: res.message, type: 'success' });
        } else {
          this.$notify({ text: (res && res.message) || this.$t('memberDataSection.updateFailed'), type: 'error' });
          this.getConnectedUsers();
        }
      } catch (e) {
        this.$notify({ text: (e && e.data && e.data.message) || this.$t('memberDataSection.updateFailed'), type: 'error' });
        this.getConnectedUsers();
      } finally {
        this.updatingAccessLevelId = null;
      }
    },
    onConnectedUserAdded(res) {
      this.addUserDialog = false;
      if (res && res.success) {
        this.$notify({ text: res.message, type: 'success' });
        this.getConnectedUsers();
      } else if (res) {
        this.$notify({ text: res.message || this.$t('memberDataSection.registrationFailed'), type: 'error' });
      }
    },
    formatAccessLevel(level) {
      if (!level) return '—';
      return level.charAt(0).toUpperCase() + level.slice(1);
    },
    connectedRowClass(item) {
      return item.isPrimary ? 'connected-primary-row' : '';
    },
    getDateFormat(date) {
      if (!date) return '-';
      
      // Try to parse the date with different formats
      let parsedDate;
      
      // First try MM/DD/YYYY format (common in the data)
      if (moment(date, 'MM/DD/YYYY', true).isValid()) {
        parsedDate = moment(date, 'MM/DD/YYYY');
      }
      // Then try ISO format
      else if (moment(date).isValid()) {
        parsedDate = moment(date);
      }
      // If all else fails, return the original date
      else {
        return date;
      }
      
      return parsedDate.format("DD/MM/YYYY");
    },

    handleAssociatedFarms() {
      // Use path directly since route name is shared with other routes
      this.$router.push({
        path: `/coffee/buying-station/${this.$route.params.id}/associated-farms`,
        params: { 
          buyingStationName: this.buyingStationData.name || 'Buying Station'
        },
      });
    },

    async handleEdit() {
      try {
        const response = await BuyingStationService.getBuyingStationDetails(this.$route.params.id);
        const station = response && response.data ? response.data : null;

        if (station) {
          // Map API response to EditAppUser expected structure (same as list view)
          this.selectedBuyingStation = {
            id: station.id,
            firstName: station.firstName || "",
            middleName: station.middleName || "",
            lastName: station.lastName || "",
            email: station.email || "",
            mobile: station.mobile || "",
            phoneNumber: station.mobile || "",
            country: station.country || "",
            countryId: station.countryId || "",
            countryCode: station.countryIsoCode || station.countryCode || "",
            stateId: station.stateId || "",
            district: station.district || "",
            village: station.village || "",
            city: station.city || "",
            address: station.address || "",
            userTribe: station.userTribe || "",
            website: station.website || "",
            id_number: station.id_number || "",
            active: Boolean(station.active),
            user_membership: Array.isArray(station.user_membership) ? station.user_membership : [],
            activation: Array.isArray(station.activation) ? station.activation : [],
          };

          this.editUserDialog = true;
        }
      } catch (err) {
        this.$notify({
          text: err?.data?.message || "Error fetching buying station details",
          type: "error",
        });
      }
    },

    handleBack() {
      // Pop history if we came from within the app; fall back to the list for direct links.
      if (this.cameFromApp) this.$router.back();
      else this.$router.push({ name: 'CoffeeBuyingStation' });
    },

    handleAddPurchaseOrder() {
      if (this.$refs.purchaseOrderList) {
        this.$refs.purchaseOrderList.openAdd();
      }
    },

    async getBuyingStationDetails() {
      this.loading = true;
      const buyingStationId = this.$route.params.id;
      
      try {
        this.startLoading()
        const res = await BuyingStationService.getBuyingStationDetails(buyingStationId);
        
        // Transform the API response to match the expected format
        const stationData = res.data;
        this.buyingStationData = {
          id: stationData.id,
          name: stationData.fullName || `${stationData.firstName || ''} ${stationData.middleName || ''} ${stationData.lastName || ''}`.trim(),
          firstName: stationData.firstName,
          middleName: stationData.middleName,
          lastName: stationData.lastName,
          email: stationData.email,
          phoneNumber: stationData.mobile,
          mobile: stationData.mobile,
          country: stationData.countryId,
          stateId: stationData.stateId,
          city: stationData.city,
          district: stationData.district,
          village: stationData.village,
          role: this.isNaccuUser ? this.$t("memberDataSection.factory") : this.$t("memberDataSection.buyingStation"), // Default role for buying stations
          status: stationData.active ? "active" : "deactivated",
          active: stationData.active,
          address: stationData.address,
          id_number: stationData.id_number
        };
        
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.$notify({
          text: "Error fetching buying station details",
          type: "error",
        });
      } finally {
        this.stopLoading()
      }
    },

    handleAddParchmentDelivery() {
      this.addParchmentDialogVisible = true;
    },
    onParchmentDeliverySaved() {
      if (this.$refs.parchmentDeliveryList) {
        this.$refs.parchmentDeliveryList.refresh();
      }
    },

    handleAddProcessingBatch() {
      if (this.$refs.processingBatchList) {
        this.$refs.processingBatchList.openAdd();
      }
    },
    handleBulkImportProcessingBatch() {
      this.$router.push({
        name: "ProcessingBatchBulkImport",
        params: { buyingStationId: String(this.buyingStationId) },
      });
    },

    // Edit User Dialog event handlers
    closeEditUserDialog() {
      this.editUserDialog = false;
      this.selectedBuyingStation = null;
    },

    handleUserUpdated(result) {
      if (result.success) {
        this.getBuyingStationDetails();
      } 
    },

  },
  mixins: [loadingMixin, getPermittedActions, DateMixin, RoleMixin],
};
</script>

<style lang="scss" scoped>
.blue-button {
  background-color: #d0e6fe !important;
  border: 2px solid #002287 !important;
  color: #002287 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

::v-deep .wdt.v-text-field {
  width: 250px;
}

/* Tab bar */
.bs-detail-tabs-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

/* Primary (owner) row in the Factory Users table */
::v-deep tr.connected-primary-row {
  background-color: rgba(0, 189, 115, 0.06);

  td:first-child {
    box-shadow: inset 3px 0 0 0 var(--primary-color, #00bd73);
  }
}

.bs-detail-tabs {
  ::v-deep .v-tabs-bar {
    background-color: #fff !important;
    height: 48px;
  }

  ::v-deep .v-slide-group__content {
    justify-content: flex-start;
  }

  ::v-deep .v-tab {
    text-transform: none;
    letter-spacing: 0;
    min-width: 120px;
    font-size: 14px;
    font-weight: 400;
    color: #6b6b6b;
    background-color: transparent;
    padding: 0 20px;

    @media (max-width: 600px) {
      min-width: 90px;
      font-size: 12px;
      padding: 0 12px;
    }
  }

  ::v-deep .v-tab--active {
    color: var(--primary-color, #944a00) !important;
    font-weight: 600;
    background-color: transparent !important;
  }

  ::v-deep .v-tabs-slider-wrapper {
    height: 3px !important;
  }

  ::v-deep .v-tabs-slider {
    background-color: var(--primary-color, #944a00) !important;
    border-radius: 2px 2px 0 0;
  }
}

::v-deep .v-tab:hover {
  color: var(--primary-color, #944a00) !important;
  background-color: transparent !important;
}

::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
}

::v-deep .center-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .img-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 40%;
}

.gray-button {
  background-color: #e8e8e8 !important;
  border: 2px solid #000000 !important;
  color: #000000 !important;
}

.green-button {
  background-color: #d4edda !important;
  border: 2px solid #28a745 !important;
  color: #28a745 !important;
}

.red-button {
  background-color: #f8d7da !important;
  border: 2px solid #dc3545 !important;
  color: #dc3545 !important;
}

/* Dynamic icon colors for rebranding */
.dynamic-icon-color {
  color: var(--primary-color, #00BD73) !important;
}

/* Indonesian client specific icon color */
[data-theme="indonesian"] .dynamic-icon-color {
  color: #184980 !important;
}

/* Kenya client specific icon color */
[data-theme="kenya"] .dynamic-icon-color {
  color: #A75300 !important;
}

/* Default client icon color */
.dynamic-icon-color {
  color: var(--primary-color, #00BD73) !important;
}

/* Text wrapping styles for long content */
.text-wrap-cell {
  max-width: 400px;
  min-width: 150px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  vertical-align: top;
  padding: 8px 12px;
}



</style>